import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { StandardHotspot } from "./StandardHotspot";
import {
  getHotspotClassNames,
  shouldAppendHotspotText,
  handleTabKeyNavigation,
} from "../utils";
import {
  sanitizeUnsafeHTML,
  sanitizeConfigOptions,
} from "@labxchange/util-sanitization";
import {
  decodeHtmlEntities,
  convertSupSubToReadableText,
  getScreenReaderFormatters,
  formatNavigationText,
} from "../../utils/stringUtils";

import className from "../../style.module.scss";

export function DropdownHotspot(props) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const hotspotRef = React.useRef();

  // When the user presses Escape on a focused hotspot, hide its tooltip while
  // keeping keyboard focus on the trigger (WCAG 1.4.13 — dismiss without moving
  // focus). Reset on blur or pointer re-entry so the tooltip is available again.
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);

  const options = props.data?.props?.options ?? [];
  const currentIndex0 = Math.max(0, (props.data?.props?.currentState ?? 1) - 1);
  const currOpt = options[currentIndex0];

  const label = useMemo(() => {
    if (props.data.tooltipText) {
      return decodeHtmlEntities(
        sanitizeUnsafeHTML(
          intl.formatMessage(
            {
              id: props.data.tooltipText,
              defaultMessage: convertSupSubToReadableText(
                props.data.tooltipText,
                intl
              ),
            },
            getScreenReaderFormatters(intl)
          ),
          sanitizeConfigOptions.PlainText
        )
      );
    }
    return intl.formatMessage({
      id: "Select an option",
      defaultMessage: "Select an option",
    });
  }, [intl, props.data.tooltipText]);

  const ariaLabel = useMemo(() => {
    // For navigation hotspots, prepend the navigation text BEFORE the label
    const formattedLabel = formatNavigationText(
      intl,
      props.data.visualOverride,
      label
    );

    // For non-navigation hotspots, keep existing logic: append "hotspot" text AFTER the label
    const hotspotText = shouldAppendHotspotText(props.data)
      ? intl.formatMessage({
          id: "hotspot",
          defaultMessage: "hotspot",
        })
      : "";
    return hotspotText ? `${formattedLabel} ${hotspotText}` : formattedLabel;
  }, [intl, label, props.data.visualOverride]);

  const handleSelect = (newIndex0) => {
    const newState1 = newIndex0 + 1;
    const currentState1 = props.data?.props?.currentState ?? 1;

    if (newState1 !== currentState1) {
      props.changeState(props.id, newState1);
      if (!props.data.triggered && props.data.postTrigger !== "no-change") {
        props.handleTrigger(props.id);
      }
    }
  };

  const isDisabled =
    (props.data.triggered && props.data.postTrigger === "inactive") ||
    (props.locked && !props.data.triggered) ||
    (currOpt?.correctness === "Correct" &&
      props.data.triggered &&
      props.data.postTrigger !== "no-change");

  const isVisible =
    props.data.visibility_dependencies.views.includes(props.currentView) ||
    props.data.visibility_dependencies.views.length === 0;

  const containerStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    cursor: isDisabled ? "default" : "pointer",
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenu.Trigger asChild>
        <div
          ref={hotspotRef}
          id={`hotspotBtn_${props.id}`}
          className={getHotspotClassNames(props, className)}
          style={containerStyle}
          tabIndex={isVisible && !isDisabled ? 0 : -1}
          role="button"
          aria-disabled={isDisabled}
          aria-label={ariaLabel}
          disabled={isDisabled}
          onMouseEnter={() => setTooltipSuppressed(false)}
          onBlur={() => setTooltipSuppressed(false)}
          onKeyDown={(e) => {
            // Handle Tab key navigation for triggered hotspots
            if (e.key === "Tab") {
              handleTabKeyNavigation(
                e,
                props.id,
                props.data,
                props.hotspotOrder,
                props.allHotspots,
                props.currentView
              );
              return;
            }

            // Handle Escape key: dismiss the tooltip WITHOUT moving focus
            // (WCAG 1.4.13). The previous hotspotRef.current.blur() dropped
            // focus to the iframe body ("main frame"); mirror the
            // StandardHotspot path and suppress the tooltip while keeping
            // keyboard focus on the trigger.
            if (e.key === "Escape" && !open) {
              if (
                hotspotRef.current &&
                document.activeElement === hotspotRef.current
              ) {
                setTooltipSuppressed(true);
              }
            }
          }}
        >
          <StandardHotspot {...props} tooltipSuppressed={tooltipSuppressed} />
        </div>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={className.dropdownContent}
          align="center"
          sideOffset={5}
          role="menu"
        >
          <DropdownMenu.Arrow className="DropdownMenuArrow" />
          {options.map((opt, i) => {
            const isSelected = i === currentIndex0;
            return (
              <DropdownMenu.Item
                key={i}
                className={`${className.dropdownItem} ${
                  isSelected ? className.dropdownItemSelected : ""
                }`}
                onSelect={() => handleSelect(i)}
                disabled={isDisabled}
                role="menuitemradio"
                aria-checked={isSelected}
              >
                <span
                  className={`${className.radioIndicator} ${
                    isSelected ? className.radioIndicatorSelected : ""
                  }`}
                  aria-hidden="true"
                />
                {opt?.name ?? i + 1}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
